import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { createStore, createHook, createContainer } from "react-sweet-state";
import { siteData } from "../../constants";
import { GlobalAcf, GlobalData, SortModes, WPPost, WPTaxTerm } from "../../constants/types";
import { actions } from "./actions";
import { Actions, PopupStates, State } from "./types";

const defaultShowingRegionSelect =
    new URL(window.location.href).searchParams.get("select-region") === "true";

const initialState: State = {
    global: {
        site: siteData,
        acf: undefined,
        regions_mapping: undefined,
    },
    ui: {
        sortMode: SortModes.DistanceFromSuburb,
        locationAccessDenied: false,
        showingFreeServices: false,
        showingAssetmentFilter: false,
        outsideAuPopup: PopupStates.PENDING,
        legacyBrowserPopup: PopupStates.PENDING,
        notificationBar: {
            hasUserClosed: false,
            enabled: false,
            content: "",
        },
    },
    routes: {},
    populations: [],
    conditions: [],
    challengingSituations: [],
    localInformation: [],
    featuredServices: [],
    servicesInRegion: [],
    taxCommunities: [],
    taxConditionsNeeds: [],
    taxGenders: [],
    taxSituations: [],
    showingRegionSelect: defaultShowingRegionSelect,
};

const store = createStore<State, typeof actions>({
    initialState,
    actions,
});

export const AppStateContainer = createContainer<State, Actions, { globalData: GlobalData }>(
    store,
    {
        onInit: () => ({ setState }, { globalData }) => {
            setState({ global: globalData });
        },
    },
);

export const useAppState = createHook(store);

export const useGlobalData = createHook(store, { selector: state => state.global });

export const useGlobalElements = createHook(store, {
    selector: state => {
        if (state.global.acf?.global_elements) {
            return state.global.acf?.global_elements;
        }
        throw new Error("Global elements not available yet");
    },
});

export const useGlobalAcf = createHook<State, Actions, GlobalAcf>(store, {
    selector: state => {
        if (state.global.acf) {
            return state.global.acf;
        }
        throw new Error("ACF data not available");
    },
});

export const useSiteData = createHook(store, {
    selector: state => state.global.site,
});

export const useGlobalLinks = createHook(store, {
    selector: ({ global }) => {
        if (global.acf?.global_links) {
            return global.acf?.global_links;
        }
        throw new Error("Global links data not available");
    },
});

export const useRoutesData = createHook(store, {
    selector: state => state.routes,
});
export const useCurrentRouteData = <ACFType>(path?: string) => {
    const [routes] = useRoutesData();
    const location = useLocation();
    return routes[path || location.pathname] as WPPost<ACFType>;
};

export const usePopulations = createHook(store, {
    selector: state => state.populations,
});

export const useConditions = createHook(store, {
    selector: state => state.conditions,
});

export const useChallengingSituations = createHook(store, {
    selector: state => state.challengingSituations,
});

export const usePostTypes = createHook(store, {
    selector: state => ({
        populations: state.populations,
        conditions: state.conditions,
        localInformation: state.localInformation,
        challengingSituations: state.challengingSituations,
    }),
});

const createTermSlugMap = (terms: WPTaxTerm[]) =>
    terms.reduce<Record<string, WPTaxTerm>>(
        (total, current) => ({
            ...total,
            [current.slug]: current,
        }),
        {},
    );

export const useTaxonomies = createHook(store, {
    selector: ({ taxCommunities, taxConditionsNeeds, taxGenders, taxSituations }) => {
        return {
            communities: taxCommunities,
            conditions: taxConditionsNeeds,
            genders: taxGenders,
            situations: taxSituations,
            communitiesBySlug: createTermSlugMap(taxCommunities),
            conditionsBySlug: createTermSlugMap(taxConditionsNeeds),
            situationsBySlug: createTermSlugMap(taxSituations),
            gendersBySlug: createTermSlugMap(taxGenders),
        };
    },
});

export const getFeatures = () => {
    return siteData.features || {};
};

export const useFeatures = () => {
    return getFeatures();
};

export const useBaseUrl = () => {
    const [{ installPath }] = useSiteData();
    return useMemo(() => (installPath === "/" ? "" : installPath), [installPath]);
};

export const useSortMode = createHook(store, {
    selector: state => state.ui.sortMode,
});

export const useLocationAccessDenied = createHook(store, {
    selector: state => state.ui.locationAccessDenied,
});

export const useOutsideAustraliaPopup = createHook(store, {
    selector: state => ({
        state: state.ui.outsideAuPopup,
        isShowing: state.ui.outsideAuPopup === PopupStates.OPENED,
    }),
});

export const useNotificationBar = createHook(store, {
    selector: state => state.ui.notificationBar,
});

export const useLegacyBrowserPopup = createHook(store, {
    selector: state => ({
        state: state.ui.legacyBrowserPopup,
        isShowing: state.ui.legacyBrowserPopup === PopupStates.OPENED,
    }),
});

export const useRegionSelectVisibility = createHook(store, {
    selector: state => state.showingRegionSelect,
});

export const useFreeServiceVisibility = createHook(store, {
    selector: state => state.ui.showingFreeServices,
});

export const useAssestmentFilterVisibility = createHook(store, {
    selector: state => state.ui.showingAssetmentFilter,
});

export const useRegionsNewMappings = () => {
    const [data] = useGlobalData();
    if (data.regions_mapping) {
        return data.regions_mapping;
    }
    return [];
};

export const useSuburbSelectModalData = createHook(store, {
    selector: state => {
        if (state.global?.acf) {
            return state.global.acf.suburb_select_modal;
        }
        throw new Error("Global data not yet defined");
    },
});

export const useSuburbSelectCarouselData = createHook(store, {
    selector: state => {
        if (state.global?.acf) {
            return state.global.acf.suburb_select_carousel;
        }
        throw new Error("Global data not yet defined");
    },
});